import { Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateModule } from '@ngx-translate/core';

import { SystemModulesEnum } from '@models/module/main/systemModules.enum';
import { ModuleInstanceService } from '@module-instance/services';
import { CipoStateComponent } from '@shared/components/cipo-state/state.component';
import { CipoTableOptions, DisplayFormats, FiltersToApply, TableColumn } from '@shared/components/data-list/common';
import { DataListModule } from '@shared/components/data-list/data-list.module';
import { PipesModule } from '@shared/modules/pipes.module';
import { ItvDatePipe } from '@shared/pipes/itv-date.pipe';
import { DocumentService, GeneralDocumentData } from '@shared/services/document.service';
import { UserStore } from '@signal-store/index';
import { DocumentDetailsStore, ProgressPaymentTabRow, ProgressPaymentTabService } from '../../common';

@Component({
  selector: 'cipo-progress-payments-tab',
  imports: [
    DataListModule,
    MatButtonModule,
    MatIconModule,
    FlexLayoutModule,
    MatMenuModule,
    TranslateModule,
    CipoStateComponent,
    PipesModule,
  ],
  templateUrl: './progress-payments-tab.component.html',
})
export class ProgressPaymentsTabComponent implements OnInit {
  private moduleInstanceService = inject(ModuleInstanceService);
  private ppTabService = inject(ProgressPaymentTabService);
  private dmStore = inject(DocumentDetailsStore);
  private documentService = inject(DocumentService);
  private itvDatePipe = inject(ItvDatePipe);
  protected userStore = inject(UserStore);
  readonly DISPLAY_FORMATS = DisplayFormats;

  entityInstanceId = input<number>();
  currentDocument = computed(() => this.dmStore.documents()[this.entityInstanceId()]);

  filtersToApply = signal<FiltersToApply>({});
  rows = signal<ProgressPaymentTabRow[]>(null);
  filters = signal(this.ppTabService.getFilters());
  viewType = signal<'table' | 'grid'>('table');

  tableOptions = signal<CipoTableOptions<any, ProgressPaymentTabRow>>({
    clickableRows: true,
    pagination: { pageSize: 5 },
  });
  tableData = computed(() => this.ppTabService.filterTableRows(this.rows(), this.filtersToApply()));
  tableColumns = signal<TableColumn[]>(this.ppTabService.getTableColumns());

  ngOnInit(): void {
    const { contractId, entityInstanceId } = this.currentDocument().params;
    this.moduleInstanceService
      .getEntityInstanceProgressPayments({
        contractId,
        parentEntityInstanceId: entityInstanceId,
      })
      .subscribe(data =>
        this.rows.set(data.map(row => ({ ...row, progress_payment_period: this.fillProgressPaymentPeriod(row) }))),
      );
  }

  openDocument(row: ProgressPaymentTabRow) {
    const doc: GeneralDocumentData = {
      moduleId: SystemModulesEnum.ProgressPayment,
      contractId: this.currentDocument().params.contractId,
      paymentPeriodId: row.payment_period_id,
      entityInstanceId: row.parent_entity_instance_id,
    };
    this.documentService.openDocument(doc);
  }

  setViewType(view: 'table' | 'grid') {
    this.viewType.set(view);
  }

  private fillProgressPaymentPeriod(row: ProgressPaymentTabRow): string {
    const startDate = this.itvDatePipe.transform(row.progress_payment_start_date);
    const endDate = this.itvDatePipe.transform(row.progress_payment_end_date);
    return `${startDate} - ${endDate}`;
  }
}
